// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xIsAuthenticated } from "@flip/App/Store/Selectors/GeneralSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasOnSuccess, makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import { CartLineModel, ProductModel, UnitOfMeasureModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import * as cssLinter from "css";
import React, { useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    containerId = "containerId",
    containerClass = "containerClass",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.containerId]: string;
        [fields.containerClass]: string;
        [fields.customCSS]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => {
    const {
        productSettings: { canAddToCart, allowBackOrder, showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);
    return {
        canAddToCart,
        allowBackOrder,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        isAuthenticated: xIsAuthenticated(state),
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    };
};

const mapDispatchToProps = {
    loadProduct: makeHandlerChainAwaitable<
        Parameters<typeof loadProduct>[0] & HasOnSuccess<ProductModel>,
        ProductModel
    >(loadProduct),
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0] & HasOnSuccess<CartLineModel>, CartLineModel>(
        addToCart,
    ),
};

export interface ProtonDataLayerWidgetStyles {}

export const protonDataLayerWidgetStyles: ProtonDataLayerWidgetStyles = {};

const styles = protonDataLayerWidgetStyles;

const ProtonDataLayerWidget = ({
    fields: { containerId, containerClass, customCSS },
    canAddToCart,
    allowBackOrder,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    isAuthenticated,
    signInPageUrl,
    loadProduct,
    addToCart,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const history = useHistory();

    useEffect(() => {
        if (typeof window !== "undefined" && !(window as any).protonAddToCart) {
            (window as any).protonAddToCart = async (productId: string, qtyOrdered: number, unitOfMeasure: string) => {
                try {
                    if (!productId || !unitOfMeasure) {
                        throw new Error("Invalid Data");
                    }
                    if (qtyOrdered <= 0) {
                        throw new Error("Invalid Qty");
                    }
                    const product = await loadProduct({ id: productId, unitOfMeasure });
                    const validation =
                        isAuthenticated &&
                        canAddToCart &&
                        !product?.cantBuy &&
                        product?.unitOfMeasures?.some(
                            (unit: UnitOfMeasureModel) => unit.unitOfMeasure.toLowerCase() === unitOfMeasure,
                        ) &&
                        (product.canAddToCart || (allowBackOrder && !product.canConfigure));
                    if (product && validation) {
                        const cartline = await addToCart({
                            productId,
                            qtyOrdered,
                            unitOfMeasure,
                        });
                        if (showAddToCartConfirmationDialog) {
                            toasterContext.addToast({
                                body: (
                                    <ProductAddedToCartMessage
                                        isQtyAdjusted={cartline.isQtyAdjusted}
                                        multipleProducts={false}
                                    />
                                ),
                                messageType: "success",
                                timeoutLength: cartline.isQtyAdjusted
                                    ? 1000 * 60 * 60 * 24 * 365
                                    : addToCartPopupTimeout,
                            });
                        }
                    } else if (!isAuthenticated) {
                        history.push(signInPageUrl || "/SignIn");
                    } else {
                        throw new Error();
                    }
                } catch (error) {
                    toasterContext.addToast({
                        body: (error as any)?.message || (error as any)?.errorMessage || siteMessage("General_Error"),
                        messageType: "danger",
                    });
                    return error;
                }
            };
        }
    }, []);

    const customCssWrapper = {
        css: css`
            ${customCSS}
        `,
    };

    return <StyledWrapper id={containerId} className={containerClass} {...customCssWrapper}></StyledWrapper>;
};

const defaultCustomCss = `.proton-widget {
}
`;

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProtonDataLayerWidget),
    definition: {
        displayName: "Proton Data Layer Widget",
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.containerId,
                displayName: "Container Id",
                editorTemplate: "TextField",
                defaultValue: "proton-widget",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.containerClass,
                displayName: "Container Class",
                editorTemplate: "TextField",
                defaultValue: "proton-widget",
                fieldType: "Translatable",
                isRequired: false,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
